import { SelectOption } from '../../ui/gzDropdown/types';
import { BaseWebComponentController, WebComponentEvent } from '../../framework/BaseWebComponentController';
import { PoliciesSelectorWebComponent } from '../component/PoliciesSelectorWebComponent';
import { EditableModuleAndSection, Policy } from '../types';
import { PoliciesSelectorRepository } from '../repository/PoliciesSelectorRepository';
import { PoliciesSelectorView } from '../view/PoliciesSelectorView';
import { templatePoliciesSelector } from '../view/template';
import { GzDropdownWebComponent } from '../../ui/gzDropdown';
import { PolicySelectorWebComponentSchema } from '../types/PolicySelectorWebComponentSchema';

export class PoliciesSelectorController extends BaseWebComponentController {
  public component: PoliciesSelectorWebComponent;
  public dataRepository: PoliciesSelectorRepository;
  public view: PoliciesSelectorView;
  public viewConstructor: typeof PoliciesSelectorView;
  public dataRepositoryConstructor: typeof PoliciesSelectorRepository;

  private searchStringToPolicies: Map<string, Array<Policy>>;

  private static getPoliciesSelectorOptions(Policies: Array<Policy> | undefined): Array<SelectOption> {
    return Policies
      ? Policies.map(policy => {
          return {
            title: policy.label,
            value: policy.value,
          };
        })
      : [];
  }

  constructor(component: PoliciesSelectorWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PoliciesSelectorView,
      viewTemplate: templatePoliciesSelector,
      dataRepositoryConstructor: PoliciesSelectorRepository,
    });

    this.searchStringToPolicies = new Map<string, Array<Policy>>();

    // This is the initial state of the selector, corresponding to empty search string. Put it in the cache.
    this.searchStringToPolicies.set('', component.policy);
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$policiesSelectorDropdown,
        event: 'selectionChanged',
        callback: this.onSelectedPolicyChanged,
      },
      {
        element: this.view.dom.$policiesSelectorDropdown,
        event: 'onNewInputString',
        callback: this.onNewInputString,
      },
    ];
  }

  async onSelectedPolicyChanged(ev: WebComponentEvent) {
    const selectedOption = ev.detail;

    // Save the previous active Policy, so we can reset it in the dropdown if we get an error.
    const previousTarget = this.component.selectedTarget;
    const currentTarget = {
      value: selectedOption.value,
      label: selectedOption.label,
    };

    if (currentTarget.value === this.component.selectedTarget.value) {
      return false;
    }

    try {
      this.component.selectedTarget = currentTarget;

      const PolicyClickedHandler = this.component.onPolicyClicked;
      if (PolicyClickedHandler) {
        await PolicyClickedHandler(currentTarget);
      }

      const PolicyChangedWithSuccessHandler = this.component.onPolicyChangedWithSuccess;
      if (PolicyChangedWithSuccessHandler) {
        await PolicyChangedWithSuccessHandler();
        return true;
      }
    } catch (error) {
      console.error(error);

      // Revert the previously selected Policy.
      this.component.selectedTarget = previousTarget;
      const lastSelected = {
        title: previousTarget.label,
        value: previousTarget.value,
      };
      const selector = this.view.dom.$PoliciesSelectorDropdown;
      selector.selectedOption = lastSelected;
      selector.value = lastSelected.value;
      selector.setLastSelectedOption();

      const PolicyChangedWithErrorHandler = this.component.onPolicyChangedWithError;
      if (PolicyChangedWithErrorHandler) {
        await PolicyChangedWithErrorHandler(error);
      }

      // Show an error dialog.
      this.view.showErrorDialog(currentTarget.label);
    }

    return false;
  }

  async onNewInputString(ev: WebComponentEvent) {
    const selector = ev.target as GzDropdownWebComponent;
    selector.isError = false;
    selector.isLoading = true;

    try {
      const { searchString } = ev.detail;

      const policies = await this.getPolicies(searchString, this.component.editableModuleAndSection);
      this.component.policy = policies;

      selector.options = PoliciesSelectorController.getPoliciesSelectorOptions(policies);
    } catch (e) {
      console.error(e);
      selector.isError = true;
      selector.options = [];
    } finally {
      selector.isLoading = false;
    }

    return false;
  }

  private async getPolicies(searchString: string, editableModuleAndSection: EditableModuleAndSection): Promise<Array<Policy>> {
    if (!this.searchStringToPolicies.has(searchString)) {
      const data = await this.dataRepository.getPolicies(editableModuleAndSection);
      this.searchStringToPolicies.set(searchString, data && data.list ? data.list : []);
    }

    return this.searchStringToPolicies.get(searchString) || [];
  }

  async getDataForView(): Promise<PolicySelectorWebComponentSchema> {
    const response = await this.dataRepository.getPolicies(this.component.editableModuleAndSection);
    if (response !== undefined && response['data'] !== undefined) {
      this.component.policy = response['data'];
    }
    return {
      disabled: this.component.disabled,
      selectedTarget: this.component.selectedTarget,
      list: this.component.policy,
      excludePolicies: this.component.excludePolicies,
    };
  }
}
